// src/context/auth-context.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "./ThemeContext";

const REMEMBER_KEY = "eva_remember_me";
const SESSION_CHECK_INTERVAL_MS = 10000; // 10s

const LOGVI_API_BASE = "https://api.logvi-havox.com";
const GOOGLEMARINE_API_BASE = "https://api.eva-googlemarine.com";

function getApiBase(): string {
  if (typeof window === "undefined") {
    return LOGVI_API_BASE;
  }

  const hostname = window.location.hostname.toLowerCase();

  const isLocalhost =
    hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";

  if (isLocalhost) {
    return LOGVI_API_BASE;
  }

  if (hostname.includes("logvi-havox")) {
    return LOGVI_API_BASE;
  }

  return GOOGLEMARINE_API_BASE;
}

export type Sessao = {
  id: number;
  nome: string;
  email?: string;
  picture?: string | number | null;
  departamento?: string | null;
  funcao?: string | null;

  // aceita number ou string
  empresa_id?: string | number | null;

  // roles e flags
  roles?: string[];

  // útil para portal do solicitante
  solicitante_id?: string | number | null;

  // opcionais
  conta_id?: string | number | null;
  uid?: string | number | null;
};

type AuthContextValue = {
  loading: boolean;
  sessao: Sessao | null;
  error: string | null;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  apiBase: string;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [sessao, setSessao] = useState<Sessao | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hadValidSession, setHadValidSession] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { setTheme, setSystemTitle } = useTheme();

  const apiBase = useMemo(() => getApiBase(), []);

  // lê o "lembrar de mim" apenas 1x
  const rememberMe = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem(REMEMBER_KEY) || "false") === true;
    } catch {
      return false;
    }
  }, []);

  const clearRemember = useCallback(() => {
    if (!rememberMe) {
      try {
        localStorage.removeItem(REMEMBER_KEY);
      } catch {
        // ignora
      }
    }
  }, [rememberMe]);

  /** Aplica config (theme e systemTitle) vinda da API, se existir */
  const applyConfigFromApi = useCallback(
    (config: any) => {
      if (!config) return;

      if (config.theme) {
        try {
          setTheme(config.theme);
        } catch {
          // ignora valor inválido
        }
      }

      const stRaw = config.systemTitle;
      let st: string | null = null;

      if (Array.isArray(stRaw)) {
        st = stRaw.join(" • ");
      } else if (typeof stRaw === "string") {
        st = stRaw;
      }

      if (st) {
        setSystemTitle(st);
      }
    },
    [setTheme, setSystemTitle]
  );

  /**
   * refresh(): chama /auth/me
   */
  const refresh = useCallback(
    async (fromInterval: boolean = false) => {
      if (!fromInterval) {
        setLoading(true);
        setError(null);
      }

      try {
        const r = await fetch(`${apiBase}/auth/me`, {
          method: "GET",
          credentials: "include",
          cache: "no-store",
        });

        if (!r.ok) {
          clearRemember();
          setSessao(null);

          if (hadValidSession) {
            const path = location.pathname;
            const isOnLogin = path === "/entrar" || path === "/login";
            const isOnSessaoEncerrada = path === "/sessao-encerrada";

            if (!isOnLogin && !isOnSessaoEncerrada) {
              navigate("/sessao-encerrada", { replace: true });
            }
          }

          return;
        }

        const data = await r.json().catch(() => null);

        if (data?.ok && data?.sessao?.nome) {
          const raw = data.sessao;

          const roles: string[] = Array.isArray(raw.roles)
            ? raw.roles.map((x: any) => String(x))
            : [];

          const s: Sessao = {
            id: Number(raw.id ?? raw.uid ?? 0),
            uid: raw.uid ?? null,
            nome: String(raw.nome || ""),
            email: raw.email ?? undefined,
            picture: raw.picture ?? null,

            departamento:
              typeof raw.departamento === "string"
                ? String(raw.departamento)
                : null,

            funcao: typeof raw.funcao === "string" ? String(raw.funcao) : null,

            empresa_id:
              raw.empresa_id !== undefined && raw.empresa_id !== null
                ? raw.empresa_id
                : null,

            roles,

            solicitante_id:
              raw.solicitante_id !== undefined && raw.solicitante_id !== null
                ? raw.solicitante_id
                : null,

            conta_id:
              raw.conta_id !== undefined && raw.conta_id !== null
                ? raw.conta_id
                : null,
          };

          setSessao(s);
          setHadValidSession(true);
          applyConfigFromApi(data.config || null);
        } else {
          clearRemember();
          setSessao(null);
        }
      } catch (e: any) {
        if (!fromInterval) {
          setError(e?.message || "Erro ao carregar sessão");
        }
        setSessao(null);
      } finally {
        if (!fromInterval) {
          setLoading(false);
        }
      }
    },
    [
      apiBase,
      applyConfigFromApi,
      clearRemember,
      hadValidSession,
      location.pathname,
      navigate,
    ]
  );

  const logout = useCallback(async () => {
    try {
      await fetch(`${apiBase}/auth/logout`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      }).catch(() => null);
    } finally {
      try {
        localStorage.removeItem(REMEMBER_KEY);
        localStorage.removeItem("access_token");
      } catch {
        // ignora
      }

      setSessao(null);
      setHadValidSession(false);
      window.location.assign("/entrar");
    }
  }, [apiBase]);

  useEffect(() => {
    refresh(false);
  }, [refresh]);

  useEffect(() => {
    const id = window.setInterval(() => {
      refresh(true);
    }, SESSION_CHECK_INTERVAL_MS);

    return () => {
      window.clearInterval(id);
    };
  }, [refresh]);

  const value: AuthContextValue = {
    loading,
    sessao,
    error,
    refresh: () => refresh(false),
    logout,
    apiBase,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth deve ser usado dentro de <AuthProvider>");
  }
  return ctx;
}
